import { DateTime } from 'luxon'
import { CollectDataStepType } from 'src/constants/collect-data/enums'
import { collectDataStepTitles } from 'src/constants/document-review/constants'
import type { Customer } from 'src/interfaces/customer'
import type { DocumentReviewQueueRow } from 'src/interfaces/documentReview'
import type { McReviewQueueRow } from 'src/interfaces/mcDocumentReview'
import { resolveCustomerDisplayName } from 'src/utils/customerList'

interface StepTypeLabelInput {
  type: CollectDataStepType
  /** The step's `metadata.disableInsuranceUpload`. */
  disableInsuranceUpload: boolean
  /** `metadata.insurancePolicyProduct.insuranceProduct.name`, when one is attached. */
  insuranceProductName?: string | null
}

/**
 * Review dates arrive either as an ISO string (the contractor queue's raw SQL selects a Postgres
 * timestamp) or as unix SECONDS (how most other admin endpoints in this app serialise dates).
 * Accepting both means the column is correct either way instead of silently rendering 1970.
 */
export const parseReviewDate = (value: string | number | null | undefined): DateTime | null => {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === 'number') {
    const fromSeconds = DateTime.fromSeconds(value)

    return fromSeconds.isValid ? fromSeconds : null
  }

  const fromIso = DateTime.fromISO(value)

  if (fromIso.isValid) {
    return fromIso
  }

  const asNumber = Number(value)

  if (!Number.isFinite(asNumber)) {
    return null
  }

  const fromSeconds = DateTime.fromSeconds(asNumber)

  return fromSeconds.isValid ? fromSeconds : null
}

/**
 * Oldest first — the same order, and the same key, as the queue tables' default `finishedAt`
 * sort. The review screen's "n of total" and its advance-to-next both walk the stored queue, so
 * storing it in table order keeps them in step with what the reviewer sees. Missing dates sort as
 * 0, matching the grid's `valueGetter`. Sorts a copy: the input is about to become store state.
 */
export const sortReviewQueueByWaiting = <Row extends { finishedAt?: string | number | null }>(
  rows: Row[],
): Row[] => {
  return [...rows].sort((first, second) => {
    const firstMillis = parseReviewDate(first.finishedAt)?.toMillis() ?? 0
    const secondMillis = parseReviewDate(second.finishedAt)?.toMillis() ?? 0

    return firstMillis - secondMillis
  })
}

/** How long a document has been waiting, counted in whole calendar days. */
export const formatWaitingDays = (value: string | number | null | undefined): string => {
  const finishedAt = parseReviewDate(value)

  if (!finishedAt) {
    return '—'
  }

  const days = Math.floor(
    DateTime.now().startOf('day').diff(finishedAt.startOf('day'), 'days').days,
  )

  if (days <= 0) {
    return 'Today'
  }

  if (days === 1) {
    return '1 day'
  }

  return `${days} days`
}

/**
 * A vehicle step with insurance upload disabled collects only vehicle details, so calling it
 * "Vehicle + insurance" would tell a reviewer to look for an insurance document that was never
 * asked for. Carried over from the Retool label override.
 */
export const resolveStepTypeLabel = ({
  type,
  disableInsuranceUpload,
  insuranceProductName,
}: StepTypeLabelInput): string => {
  if (type === CollectDataStepType.VehicleInsurance && disableInsuranceUpload) {
    return 'Vehicle Information'
  }

  // An unmapped type renders its own wire value rather than a guess — a new backend step type
  // must read as itself, not as the wrong existing one.
  const title = collectDataStepTitles[type] ?? type

  if (insuranceProductName) {
    return `${title}, ${insuranceProductName}`
  }

  return title
}

export const filterContractorQueue = (
  rows: DocumentReviewQueueRow[],
  search: string,
): DocumentReviewQueueRow[] => {
  const term = search.trim().toLowerCase()

  if (term === '') {
    return rows
  }

  return rows.filter((row) => {
    const haystack = [
      row.driverName,
      row.partnerName,
      row.phone,
      row.label,
      // The rendered "Document type" column (`contractorQueueGrid.tsx`) is this label, not
      // `row.label` above — without it a reviewer searching the exact text on screen (e.g.
      // "Driver license") gets no results. `false` matches the grid column: the queue row
      // carries no step metadata to know the real `disableInsuranceUpload` value.
      resolveStepTypeLabel({ type: row.type, disableInsuranceUpload: false }),
    ]
      .filter((field): field is string => {
        return typeof field === 'string'
      })
      .join(' ')
      .toLowerCase()

    return haystack.includes(term)
  })
}

/**
 * A partnerId → display-name lookup for the MC queue.
 *
 * The MC rows carry only `partnerId`, and there is no MC endpoint that returns a name, so the
 * name is joined against the customer list the admin already loads. Built once per customer-list
 * change and passed in, rather than resolved per row, so a 200-row grid does not run 200 scans.
 */
export const buildCustomerNameById = (customers: Customer[]): Record<string, string> => {
  return customers.reduce<Record<string, string>>((accumulator, customer) => {
    accumulator[customer.id] = resolveCustomerDisplayName(customer)

    return accumulator
  }, {})
}

/**
 * An unresolved partner renders its own id, never a placeholder name. The customer list can be
 * absent because it has not loaded yet OR because this reviewer lacks `partners_view` — and a
 * fabricated name would hide both cases behind something that looks like real data.
 */
export const resolveMcDisplayName = (
  partnerId: string,
  customerNameById: Record<string, string>,
): string => {
  return customerNameById[partnerId] ?? partnerId
}

export const filterMcQueue = (
  rows: McReviewQueueRow[],
  search: string,
  customerNameById: Record<string, string>,
): McReviewQueueRow[] => {
  const term = search.trim().toLowerCase()

  if (term === '') {
    return rows
  }

  return rows.filter((row) => {
    const haystack = [resolveMcDisplayName(row.partnerId, customerNameById), row.type, row.status]
      .filter((field): field is string => {
        return typeof field === 'string'
      })
      .join(' ')
      .toLowerCase()

    return haystack.includes(term)
  })
}
